import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Loader2, Users, Hash } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/**
 * CreateSpaceModal - Modal for creating a new space
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close callback
 * @param {object} user - Current auth user
 * @param {function} onSpaceCreated - Callback when space is created
 */
export default function CreateSpaceModal({ isOpen, onClose, user, onSpaceCreated }) {
  const [mode, setMode] = useState('create'); // 'create' | 'join'
  const [spaceName, setSpaceName] = useState('');
  const [description, setDescription] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    if (!spaceName.trim()) {
      setError('Please enter a space name');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Use the database function to create space with defaults
      const { data, error: createError } = await supabase
        .rpc('create_space_with_defaults', {
          space_name: spaceName.trim(),
          space_description: description.trim() || null,
        });

      if (createError) throw createError;

      // Fetch the created space
      const { data: spaceData, error: fetchError } = await supabase
        .from('spaces')
        .select(`
          *,
          members!inner(user_id, role),
          channels(*)
        `)
        .eq('id', data)
        .single();

      if (fetchError) throw fetchError;

      // Format space for the UI
      const formattedSpace = {
        id: spaceData.id,
        name: spaceData.name,
        description: spaceData.description,
        icon_url: spaceData.icon_url,
        invite_code: spaceData.invite_code,
        memberCount: spaceData.members?.length || 1,
        channels: spaceData.channels || [],
        unreadMentions: 0,
        unreadMessages: 0,
      };

      if (onSpaceCreated) {
        onSpaceCreated(formattedSpace);
      }

      // Reset form
      setSpaceName('');
      setDescription('');
      onClose();
    } catch (err) {
      console.error('Space creation error:', err);
      setError('Failed to create space. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Use the database function to join by invite code
      const { data: spaceId, error: joinError } = await supabase
        .rpc('join_space_by_invite', {
          invite_code_param: inviteCode.trim(),
        });

      if (joinError) {
        if (joinError.message.includes('Invalid invite code')) {
          setError('Invalid invite code. Please check and try again.');
          setSaving(false);
          return;
        }
        throw joinError;
      }

      // Fetch the joined space
      const { data: spaceData, error: fetchError } = await supabase
        .from('spaces')
        .select(`
          *,
          members(user_id),
          channels(*)
        `)
        .eq('id', spaceId)
        .single();

      if (fetchError) throw fetchError;

      const formattedSpace = {
        id: spaceData.id,
        name: spaceData.name,
        description: spaceData.description,
        icon_url: spaceData.icon_url,
        memberCount: spaceData.members?.length || 1,
        channels: spaceData.channels || [],
        unreadMentions: 0,
        unreadMessages: 0,
      };

      if (onSpaceCreated) {
        onSpaceCreated(formattedSpace);
      }

      setInviteCode('');
      onClose();
    } catch (err) {
      console.error('Join space error:', err);
      setError('Failed to join space. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setMode('create');
    setSpaceName('');
    setDescription('');
    setInviteCode('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-lg bg-gjyl-dark rounded-t-3xl overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">
                {mode === 'create' ? 'Create Space' : 'Join Space'}
              </h2>
              <motion.button
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
                onClick={handleClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={20} />
              </motion.button>
            </div>

            {/* Mode Tabs */}
            <div className="flex px-6 pt-4">
              <button
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  mode === 'create'
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => { setMode('create'); setError(null); }}
              >
                Create New
              </button>
              <button
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                  mode === 'join'
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => { setMode('join'); setError(null); }}
              >
                Join Existing
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-4">
              {mode === 'create' ? (
                <>
                  {/* Space Icon Preview */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center">
                      <Users size={32} className="text-white" />
                    </div>
                  </div>

                  {/* Space Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Space Name
                    </label>
                    <input
                      type="text"
                      value={spaceName}
                      onChange={(e) => setSpaceName(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      placeholder="My Awesome Space"
                      maxLength={100}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description <span className="text-gray-500">(optional)</span>
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                      placeholder="What's this space about?"
                      rows={3}
                    />
                  </div>

                  {/* Default Channel Info */}
                  <div className="flex items-center space-x-2 text-gray-400 text-sm p-3 bg-zinc-900/50 rounded-xl">
                    <Hash size={16} />
                    <span>A #general channel will be created automatically</span>
                  </div>
                </>
              ) : (
                <>
                  {/* Join Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                      <Users size={32} className="text-white" />
                    </div>
                  </div>

                  {/* Invite Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Invite Code
                    </label>
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 font-mono"
                      placeholder="Paste invite code here"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Ask a space member for their invite code
                    </p>
                  </div>
                </>
              )}

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-300 text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 pb-8 border-t border-white/10">
              <motion.button
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50"
                onClick={mode === 'create' ? handleCreate : handleJoin}
                disabled={saving || (mode === 'create' ? !spaceName.trim() : !inviteCode.trim())}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>{mode === 'create' ? 'Creating...' : 'Joining...'}</span>
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    <span>{mode === 'create' ? 'Create Space' : 'Join Space'}</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
